<!-- eslint-disable vue/first-attribute-linebreak -->
<!-- eslint-disable prettier/prettier -->
<template>
  <Dialog
    ref="dialogRef"
    dialog-mode="medium"
    content-class="dialog-content"
    header-class="font-semibold text-[20px]"
    class="dialog-min-height"
    dialog-class="mobile-full"
    @close="onFormClosed"
  >
    <template #title>{{ dialogTitle ?? formContainer?.properties.title }}</template>
    <template #content>
      <div v-if="!formContainer" class="flex min-h-[400px] items-center justify-center">
        <VcIcon
          name="loading-3quarters"
          path="/static/thorlabs/icons/family-page/"
          size="xxl"
          class="spin-animation fill-[--color-neutral-a2]"
        />
      </div>
      <div v-else :class="descriptionClass">
        <slot name="description"></slot>
        <Form
          ref="formRef"
          :key="formKey"
          :form="formContainer"
          :form-name="formName"
          :product="product"
          :details="details"
          :mail-to="mailTo"
          :contact-us-form-name="props.contactUsFormName"
          :order-number="props.orderNumber"
          @submitted="onFormSubmitted"
        />
      </div>
    </template>
    <template #footer>
      <VcButton color="primary"
                variant="solid"
                class="w-full"
                size="lg"
                :loading="formState === 'submitting'"
                @click="dialogRef && submitForm()">
        {{ submitButtonText }}
      </VcButton>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
/* eslint-disable prettier/prettier */
import { provideApolloClient } from "@vue/apollo-composable";
import { useDebounceFn } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { apolloClient } from "@/core/api/graphql";
import { Logger } from "@/core/utilities";
import { useForm } from "@/shared/common/composables/useForm";
import { useThorlabsGtm } from "@/shared/gtm/gtm";
import { FormServerValidationError, FormValidationError } from "../utils/formValidationError";
import Form from "./form.vue";
import type { FormElementPropertiesBase } from "../forms-sdk";
import type { ExtendedFormContainer } from "../types";
import type { Product } from "@/core/api/graphql/types";
import Dialog from "@/shared/thorlabs/shared/components/dialog-modal.vue";
import VcIcon from "@/ui-kit/components/atoms/icon/vc-icon.vue";

interface IProps {
  formName: string;
  product?: Product;
  details?: string;
  dialogTitle?: string;
  dialogButton?: string;
  descriptionClass?: string;
  mailTo?: string;
  shouldSendGtmGenerateLeadEvent?: boolean; //Flag whether to send GTM event on form submission
  contactUsFormName?: string;
  fieldPropertiesOverrides?: FieldPropertiesOverride[];
  orderNumber?: string;
}

const props = defineProps<IProps>();
const fieldPropertiesOverrideRef = computed(() => props.fieldPropertiesOverrides);

const dialogRef = ref<InstanceType<typeof Dialog> | null>(null);
//this is used to remount the component after submit/close
const formKey = ref(0);

const formContainer = ref<ExtendedFormContainer | undefined>(undefined);
const formRef = ref<InstanceType<typeof Form> | undefined>(undefined);
const formName = computed(() => props.formName);

const { gtmSendGenerateLeadEvent, gtmSendFeedbackFormClosed } = useThorlabsGtm();
const contactUsCustomizationTriggered = ref(false);

const formState = ref<"initial" | "submitting" | "submitted" | "error">("initial");

const { t } = useI18n();

export type FieldPropertiesOverride = {
  displayName: string;
  properties: { key: keyof FormElementPropertiesBase; value: string }[];
};

const submitButtonText = computed(() =>
{
  if (formState.value === "error")
  {
    return t("common.buttons.form_submit_failed");
  }
  else if (formState.value === "submitting")
  {
    return t("common.buttons.submitting");
  }
  else if (formState.value === "submitted")
  {
    return t("common.buttons.form_submitted");
  }

  //For form initial state use this
  return props.dialogButton ?? "Submit";
});

const fieldPropertiesOverrideWatch = () => {
  watch(
    [formContainer, fieldPropertiesOverrideRef],
    ([newFormContainer, newFieldPropertiesOverride]) => {
      if (contactUsCustomizationTriggered.value) {
        return; // Skip if already triggered
      }
      const allFieldPropertiesOverridesHaveValues = newFieldPropertiesOverride?.every((override) =>
        override?.properties?.reduce((acc, property) => acc && !!property.value, true),
      );
      if (!newFormContainer || !allFieldPropertiesOverridesHaveValues) {
        return;
      }

      newFieldPropertiesOverride?.forEach((override) => {
        override?.properties?.forEach((property) => {
          const fieldIndex = newFormContainer.formElements.findIndex((el) => el.displayName === override.displayName);
          newFormContainer.formElements[fieldIndex].properties[property.key] = property.value;
        });
      });
      formContainer.value = {
        ...newFormContainer,
        formElements: [...newFormContainer.formElements],
      };
      contactUsCustomizationTriggered.value = true;
    },
    { immediate: true },
  );
};

const lastProcessedFormName = ref<string | null>(null);
const showDialog = computed(() => () => {
  const hasFormNameChanged = lastProcessedFormName.value !== formName.value;

  if (!formContainer.value || hasFormNameChanged) {
    lastProcessedFormName.value = formName.value;

    const { formContainer: useFormContainer } = provideApolloClient(apolloClient)(() => useForm(formName.value));

    watch(
      useFormContainer,
      (newValue) => {
        formContainer.value = newValue;
      },
      { immediate: true },
    );
    fieldPropertiesOverrideWatch();
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  dialogRef.value?.openDialog();
});

async function submitForm(): Promise<void>
{
  if (!formRef.value)
  {
    Logger.warn("Form reference is not set.");
    return;
  }

  formState.value = "submitting";

  try
  {
    await formRef.value?.submit();

    if (props.shouldSendGtmGenerateLeadEvent) {
      gtmSendGenerateLeadEvent(props.contactUsFormName ?? props.formName);
    }
  }
  catch(error)
  {
    if (error instanceof FormValidationError || error instanceof FormServerValidationError) {
      // The rejected fields are highlighted inline: keep the button in its initial state so the user can fix and resubmit
      formState.value = "initial";
      return;
    }

    Logger.warn("Error submitting form", error);
    formState.value = "error";
    //await closeDialogDebounced();
  }

}

const closeDialogDebounced = useDebounceFn(() =>
{
  dialogRef.value?.closeDialog();
  resetForm();
}, 2000);

async function onFormSubmitted() : Promise<void>
{
  if (formState.value === "error") {
    return; // Do not proceed if there was an error
  }

  formState.value = "submitted";
  await closeDialogDebounced();
}

function onFormClosed(): void
{
  if (formState.value === "initial") {
    gtmSendFeedbackFormClosed(props.contactUsFormName ?? props.formName);
  }
  resetForm();
}

function resetForm(): void {
  formKey.value++;
  formState.value = "initial";
}

defineExpose({
  showDialog: showDialog.value,
  showDialogRef: showDialog,
});
</script>

<style scoped>
.dialog-min-height {
  min-height: 400px;
}
</style>
